import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  SidePanel,
  SidePanelType,
  SidePanelLoader,
  IconArrowRight,
  InputText,
  IconSearch,
  Popup,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { setSearchText } from '../hoc/store/slices/message';
import { useAssignEmployeeToChatMutation } from '../hoc/store/services';
import { AssignSidePanelTypes } from './types';

import cn from './messages.module.css';

const AssignSidePanel = ({
  panelIsOpen,
  setAssignPanelIsOpen,
  employees,
  isLoading,
  messageId,
  path,
  onSearch,
}: AssignSidePanelTypes) => {
  const [popupVisible, setPopupVisible] = useState(false);
  const [assignedUser, setAssignedUser] = useState<{
    id: string;
    name: string;
  }>();

  const navigate = useNavigate();

  const { searchText } = useAppSelector((state) => state.messages.message);
  const [assignToChat, { isLoading: assignIsLoading, isSuccess }] =
    useAssignEmployeeToChatMutation();

  const assignToChatAction = () => {
    assignToChat({
      id: messageId as string,
      body: {
        employeeId: assignedUser?.id as string,
      },
    });
  };

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (isSuccess && path) {
      setPopupVisible(false);
      setAssignedUser(undefined);
      setAssignPanelIsOpen(false);

      navigate(path);
    }
  }, [isSuccess, path]);

  let content;

  if (isLoading) {
    content = <SidePanelLoader />;
  } else {
    content = (
      <>
        <InputText
          name="search"
          placeholder="Enter the employee's name..."
          labelClassName={cn.searchEmployeeInput}
          prefix={<IconSearch />}
          value={searchText}
          onChange={(e) => {
            onSearch(e.target.value);
            dispatch(setSearchText(e.target.value));
          }}
        />
        <div className={cn.employeesList}>
          {employees?.map((item) => (
            <button
              type="button"
              className={cn.employeeButton}
              key={item.id}
              onClick={() => {
                setAssignedUser({
                  id: item.id,
                  name: `${item.profile?.firstName} ${item.profile?.lastName}`,
                });
                setPopupVisible(true);
              }}
              aria-label="Employee"
            >
              <div className={cn.employeeButtonLeft}>
                <div className={cn.employeeButtonTitle}>
                  {item.highlight ||
                    `${item?.profile?.firstName} ${item?.profile?.lastName}`}
                </div>
                <div className={cn.employeeButtonEmail}>{item?.email}</div>
              </div>
              <div className={cn.employeeButtonRight}>
                <IconArrowRight />
              </div>
            </button>
          ))}
          {!employees?.length && (
            <div className={cn.emptyEmployee}>No employees yet</div>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <Popup
        title={`Assign an ${assignedUser?.name}`}
        onCancelClick={() => {
          setPopupVisible(false);
          setAssignedUser(undefined);
        }}
        onOkClick={() => assignToChatAction()}
        cancelButtonText="Cancel"
        okButtonText={assignIsLoading ? 'Loading...' : 'Assign'}
        isVisible={popupVisible}
        parent={document.getElementById('employee-popup') as HTMLElement}
      />
      <SidePanel
        type={SidePanelType.Fees}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={() => {
          setAssignPanelIsOpen(false);
          dispatch(setSearchText(''));
          onSearch('');
        }}
        title="Assign chat"
        sidepanelExpandedKey="assign-panel-messages"
      >
        {content}
      </SidePanel>
    </>
  );
};

export default AssignSidePanel;
